import type { Location as RouterLocation } from 'react-router';
import { z } from 'zod';

import { parseReturnPath } from '@/lib/routes';

type RedirectLocation = Pick<RouterLocation, 'pathname' | 'search' | 'hash'>;
type RedirectSearchParams = Pick<URLSearchParams, 'get'>;

export function buildRedirectPath(location: RedirectLocation): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

/**
 * Router state a guard leaves when it sends the user elsewhere first (sign-in,
 * terms, onboarding): where they were headed. Guards write the path; a location
 * object is read too, as history entries can still hold one.
 */
const redirectStateSchema = z.object({
  from: z.union([
    z.string(),
    z
      .object({ pathname: z.string(), search: z.string(), hash: z.string() })
      .transform(buildRedirectPath),
  ]),
});

/** The state a guard navigates with so the page it sends the user to can send them back. */
export function buildRedirectState(location: RedirectLocation): z.input<typeof redirectStateSchema> {
  return { from: buildRedirectPath(location) };
}

function parseRedirectTarget(value: string, fallback: string): string {
  return parseReturnPath(value.trim()) ?? fallback;
}

export function readRedirectPathFromSearchParams(
  searchParams: RedirectSearchParams,
  fallback = '/',
): string {
  const redirectTarget = searchParams.get('redirect');
  return redirectTarget === null ? fallback : parseRedirectTarget(redirectTarget, fallback);
}

export function readRedirectPath(state: RouterLocation['state'], fallback = '/'): string {
  const redirect = redirectStateSchema.safeParse(state);
  return redirect.success ? parseRedirectTarget(redirect.data.from, fallback) : fallback;
}
